import React, { FC } from "react";
import {
  Modal,
  Box,
  Typography,
  ModalProps,
  ButtonProps,
  SxProps,
  Theme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { globalStyles } from "../styles/GlobalStyles";
import Button from "./Button";

type CommonModalProps = {
  open?: boolean;
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  iconBg?: string;
  buttonArray?: Array<
    {
      text: string;
    } & ButtonProps
  >;
  containerStyle?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
  descriptionSx?: SxProps<Theme>;
  buttonWrapperSx?: SxProps<Theme>;
  children?: React.ReactNode;
} & Omit<ModalProps, "children">;

const CommonModal: FC<CommonModalProps> = ({
  open = false,
  title,
  description,
  icon,
  iconBg = "transparent",
  buttonArray,
  containerStyle,
  titleSx,
  descriptionSx,
  buttonWrapperSx,
  children,
  ...props
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      {...props}
    >
      <Box
        p={4}
        sx={[globalStyles.modalContainer, containerStyle] as SxProps<Theme>}
      >
        {icon && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 56,
              height: 56,
              borderRadius: "50%",
              backgroundColor: iconBg,
              mb: 2.5,
              "& svg": { width: 24, height: 24 },
            }}
          >
            {icon}
          </Box>
        )}
        {title && (
          <Typography
            textAlign="center"
            id="modal-modal-title"
            fontWeight={700}
            fontSize={18}
            sx={titleSx}
          >
            {t(title)}
          </Typography>
        )}

        {description && (
          <Typography
            textAlign="center"
            id="modal-modal-description"
            fontSize={13}
            mt={1.5}
            sx={descriptionSx}
          >
            {t(description)}
          </Typography>
        )}

        {children}

        {buttonArray && (
          <Box
            gap={2}
            sx={[globalStyles.rowCenter, buttonWrapperSx] as SxProps<Theme>}
            mt={4}
          >
            {buttonArray.map((itm, index) => {
              const { text, sx, variant, ...btnProps } = itm;
              return (
                <Button
                  key={`${text}-${index}`}
                  variant={variant || "contained"}
                  sx={{
                    fontSize: 16,
                    height: 40,
                    ...sx,
                  }}
                  {...btnProps}
                >
                  {t(text)}
                </Button>
              );
            })}
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default CommonModal;
